import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { DataResult, ColoredThreshold, WidgetSettingsDto } from '@thingslog/repositories';
import { useTranslation } from 'react-i18next';
import { Widget } from './Widget';
import { DataResultParser } from './parsing/DataResultParser';
import { DataResultParseStream } from './parsing/DataResultParsed';
import { ChartEmptyState, isColorDark } from './utils';
import { WidgetSettingsFactory } from '../../factories/WidgetSettings.factory';

const VALUE_COLORS = {
  normal: '#3B82F6',
  textDark: '#0F172A',
  textLight: '#F8FAFC',
};

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const VALUE_FONT_MIN_PX = 16;
// Fraction of the container height the value may occupy.
const VALUE_FONT_HEIGHT_RATIO = 0.5;
// Approximate advance width of a tabular digit relative to the font size; used
// to shrink long numbers so they fit the container width.
const VALUE_FONT_CHAR_WIDTH_RATIO = 0.62;
// Stacked prefix/suffix and the target-percentage line render at this fraction
// of the value's font size.
const AFFIX_FONT_RATIO = 0.4;
const AFFIX_FONT_MIN_PX = 12;
// Tailwind p-3 on the tile; subtracted from the measured tile size to get the
// space actually available for content.
const TILE_PADDING_PX = 12;

const COMPACT_TIERS: Array<{ threshold: number; suffix: string }> = [
  { threshold: 1e12, suffix: 'T' },
  { threshold: 1e9, suffix: 'B' },
  { threshold: 1e6, suffix: 'M' },
  { threshold: 1e3, suffix: 'k' },
];

interface ExtractedValue {
  numeric: number;
  display: string;
}

interface ValuePercent {
  percent: number;
  aboveTarget: boolean;
}

interface ValueContentSize {
  height: number;
  width: number;
}

interface ValueContentProps {
  backgroundColor: string;
  formattedValue: string;
  percent: ValuePercent | null;
  prefix: string;
  stacked: boolean;
  suffix: string;
  textColor: string;
}

const ValueContent = ({
  backgroundColor,
  formattedValue,
  percent,
  prefix,
  stacked,
  suffix,
  textColor,
}: ValueContentProps): JSX.Element => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<ValueContentSize>({ height: 0, width: 0 });

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    let frameId: number | null = null;

    const updateSize = (): void => {
      const height = container.clientHeight;
      const width = container.clientWidth;
      // Bail out when the size is unchanged to avoid redundant re-renders.
      setSize((prev: ValueContentSize) =>
        prev.height === height && prev.width === width ? prev : { height, width }
      );
    };

    updateSize();

    // The observed element is the outer tile, whose size is imposed by the
    // dashboard grid and never depends on the content rendered inside it.
    // Measuring is additionally deferred to the next animation frame so the
    // observer callback never causes layout work in the same frame — the
    // browser reports that as "ResizeObserver loop completed with undelivered
    // notifications".
    const observer = new ResizeObserver(() => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(updateSize);
    });
    observer.observe(container);

    return (): void => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      observer.disconnect();
    };
  }, []);

  const percentText = percent
    ? percent.aboveTarget
      ? t('dashboard_value_percent_above_target', '{{percent}}% above target', {
          percent: percent.percent,
        })
      : t('dashboard_value_percent_of_target', '{{percent}}% of target', {
          percent: percent.percent,
        })
    : null;

  const hasPrefix = prefix.length > 0;
  const hasSuffix = suffix.length > 0;
  const inlineText = [prefix, formattedValue, suffix]
    .filter((part: string) => part.length > 0)
    .join(' ');
  const contentWidth = Math.max(size.width - TILE_PADDING_PX * 2, 0);
  const contentHeight = Math.max(size.height - TILE_PADDING_PX * 2, 0);
  // Inline mode fits the whole "prefix value suffix" line; stacked mode fits
  // the value alone and shares the height budget with the stacked affixes. The
  // bottom-pinned percentage line takes a slot of the height budget too.
  const fittedText = stacked ? formattedValue : inlineText;
  const stackedAffixCount = stacked ? Number(hasPrefix) + Number(hasSuffix) : 0;
  const secondaryLineCount = stackedAffixCount + (percentText ? 1 : 0);
  const heightRatio = VALUE_FONT_HEIGHT_RATIO / (1 + AFFIX_FONT_RATIO * secondaryLineCount);

  const fontSize = Math.max(
    VALUE_FONT_MIN_PX,
    Math.min(
      contentHeight * heightRatio,
      contentWidth / (Math.max(fittedText.length, 1) * VALUE_FONT_CHAR_WIDTH_RATIO)
    )
  );
  const longestSecondaryLength = Math.max(
    prefix.length,
    suffix.length,
    percentText?.length ?? 0,
    1
  );
  const affixFontSize = Math.max(
    AFFIX_FONT_MIN_PX,
    Math.min(
      fontSize * AFFIX_FONT_RATIO,
      contentWidth / (longestSecondaryLength * VALUE_FONT_CHAR_WIDTH_RATIO)
    )
  );
  const hasSize = contentWidth > 0 && contentHeight > 0;

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full flex-col items-stretch p-3"
      style={{ backgroundColor }}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-1 overflow-hidden">
        {hasSize &&
          (stacked ? (
            <>
              {hasPrefix && (
                <span
                  className="leading-none"
                  style={{ color: textColor, fontSize: affixFontSize }}
                  title={prefix}
                >
                  {prefix}
                </span>
              )}
              <span
                className="font-semibold leading-none tabular-nums"
                style={{ color: textColor, fontSize }}
                title={formattedValue}
              >
                {formattedValue}
              </span>
              {hasSuffix && (
                <span
                  className="leading-none"
                  style={{ color: textColor, fontSize: affixFontSize }}
                  title={suffix}
                >
                  {suffix}
                </span>
              )}
            </>
          ) : (
            <span
              className="font-semibold leading-none tabular-nums"
              style={{ color: textColor, fontSize }}
              title={inlineText}
            >
              {inlineText}
            </span>
          ))}
      </div>
      {hasSize && percentText && (
        <div className="flex shrink-0 justify-center pt-1">
          <span
            className="leading-none"
            style={{ color: textColor, fontSize: affixFontSize }}
            title={percentText}
          >
            {percentText}
          </span>
        </div>
      )}
    </div>
  );
};

const toNumber = (value: string | number | undefined): number | null => {
  if (value === undefined) {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numeric) ? numeric : null;
};

const formatCompactNumber = (value: number): string => {
  const absValue = Math.abs(value);
  const tier = COMPACT_TIERS.find(
    (candidate: { threshold: number; suffix: string }) => absValue >= candidate.threshold
  );

  if (!tier) {
    return String(value);
  }

  const scaled = Math.round((value / tier.threshold) * 10) / 10;

  return `${Number.isInteger(scaled) ? scaled.toFixed(0) : scaled.toFixed(1)}${tier.suffix}`;
};

const resolveColor = (color: string | undefined, fallback: string): string => {
  return color && HEX_COLOR_PATTERN.test(color) ? color : fallback;
};

const extractValue = (dataStreams: DataResultParseStream[]): ExtractedValue | null => {
  const stream = dataStreams[dataStreams.length - 1];
  const dataPoint = stream?.data[stream.data.length - 1];
  const dataKey = stream?.visualization[stream.visualization.length - 1]?.dataKey;

  if (!dataPoint || !dataKey) {
    return null;
  }

  const raw = dataPoint[dataKey];
  const numeric = toNumber(raw);

  if (numeric === null) {
    return null;
  }

  // Show the value exactly as the server sent it — no rounding or padding.
  return { numeric, display: typeof raw === 'string' ? raw.trim() : String(raw) };
};

/**
 * The background is the color of the highest threshold the value has reached
 * (thresholds are absolute values); below the lowest threshold the base color
 * applies.
 */
const resolveValueBackgroundColor = (
  value: number,
  thresholds: ColoredThreshold[],
  baseColor: string
): string => {
  const reachedThresholds = thresholds
    .map((threshold: ColoredThreshold) => ({ ...threshold, value: Number(threshold.value) }))
    .filter((threshold: ColoredThreshold) => Number.isFinite(threshold.value))
    .filter((threshold: ColoredThreshold) => threshold.value <= value)
    .sort((a: ColoredThreshold, b: ColoredThreshold) => a.value - b.value);

  const activeThreshold = reachedThresholds[reachedThresholds.length - 1];

  return resolveColor(activeThreshold?.color, baseColor);
};

export class ValueWidget implements Widget {
  public render(data: DataResult, widgetSettings: WidgetSettingsDto): ReactNode {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);

    if (normalizedSettings.type !== 'value') {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_value_settings_missing"
          description="Value settings are missing."
        />
      );
    }

    const dataParser = new DataResultParser(normalizedSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);
    const noData = parsedData.dataStreams.every(
      (stream: DataResultParseStream) => stream.data.length === 0
    );

    if (noData) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_value"
          description="This widget has no value to display."
        />
      );
    }

    const extractedValue = extractValue(parsedData.dataStreams);

    if (extractedValue === null) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_numeric_value"
          description="This widget has no numeric value to display."
        />
      );
    }

    const visualization = normalizedSettings.visualization;
    const baseColor = resolveColor(visualization.color, VALUE_COLORS.normal);
    const backgroundColor = resolveValueBackgroundColor(
      extractedValue.numeric,
      visualization.thresholds,
      baseColor
    );
    const autoTextColor = isColorDark(backgroundColor)
      ? VALUE_COLORS.textLight
      : VALUE_COLORS.textDark;
    const textColor = resolveColor(visualization.textColor ?? undefined, autoTextColor);

    const target =
      typeof visualization.target === 'number' && Number.isFinite(visualization.target)
        ? visualization.target
        : null;
    const valueText = visualization.compact
      ? formatCompactNumber(extractedValue.numeric)
      : extractedValue.display;
    const targetText =
      target === null ? null : visualization.compact ? formatCompactNumber(target) : String(target);
    const formattedValue = targetText === null ? valueText : `${valueText}/${targetText}`;

    let percent: ValuePercent | null = null;
    if (target !== null && target > 0 && visualization.showTargetPercentage) {
      const ratio = extractedValue.numeric / target;
      percent =
        ratio <= 1
          ? { percent: Math.round(ratio * 100), aboveTarget: false }
          : { percent: Math.round((ratio - 1) * 100), aboveTarget: true };
    }

    return (
      <ValueContent
        backgroundColor={backgroundColor}
        formattedValue={formattedValue}
        percent={percent}
        prefix={visualization.prefix ?? ''}
        stacked={visualization.stacked}
        suffix={visualization.suffix ?? ''}
        textColor={textColor}
      />
    );
  }
}
